import { useState, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, message, Modal } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getPanels, deletePanel } from '@/api/panels';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { PanelTemplate } from '@/types/panel';
import {
  UploadOutlined,
  UpOutlined,
  // PictureOutlined, // ✅ 添加图片管理图标
  WarningOutlined,
  AppleOutlined,
  AndroidOutlined,
} from '@ant-design/icons';

/**
 * 面板列表页面（前端分页）
 */
export const PanelList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // ✅ 新增：管理每页显示条数的状态
  const [pageSize, setPageSize] = useState(20);

  const panelQuery = useQuery({
    queryKey: ['panels', developerId],
    queryFn: getPanels,
    enabled: !!developerId,
  });

  // 按 created_at 倒序排序（最新在前）
  const panels = useMemo(() => {
    if (!panelQuery.data) return [];
    return [...panelQuery.data].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [panelQuery.data]);

  const loading = panelQuery.isLoading;
  const error = panelQuery.error;

  /**
   * 处理删除面板（保留但移到详情页使用）
   */
  const handleDelete = (panelId: string, panelName: string): void => {
    Modal.confirm({
      title: '确认删除面板',
      icon: <WarningOutlined className="text-orange-500" />,
      content: `确定要删除面板 "${panelName}" 吗？此操作不可恢复，已关联该面板的产品将无法正常展示控制界面！`,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePanel(panelId);
          message.success('面板删除成功');
          await queryClient.invalidateQueries({ queryKey: ['panels', developerId] });
        } catch {
          message.error('请先确保没有关联到产品，再进行删除操作。');
        }
      },
    });
  };

  console.log('panels', handleDelete);

  /**
   * 处理上传面板
   */
  const handleUpload = (): void => {
    navigate('/panels/upload');
  };

  /**
   * 处理图片管理
   */
  // const handleManageImages = (panelId: string): void => {
  //   navigate(`/panels/${panelId}/images`);
  // };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert
        message="加载失败"
        description="无法加载面板列表，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  // 平台类型映射
  const platformMap: Record<string, { name: string; color: string; icon: React.ReactNode }> = {
    jsbundle: { name: 'iOS', color: 'blue', icon: <AppleOutlined className="text-xs mr-1" /> },
    bundle: { name: 'Android', color: 'green', icon: <AndroidOutlined className="text-xs mr-1" /> },
  };

  // 表格列定义
  const columns: ColumnsType<PanelTemplate> = [
    {
      title: '面板名称',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (text: string, record: PanelTemplate) => (
        <Link
          to={`/panels/${record.id}`}
          className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
        >
          {text}
        </Link>
      ),
    },
    {
      title: '适配平台',
      dataIndex: 'file_type',
      key: 'file_type',
      width: 120,
      render: (fileType: string) => {
        const platform = platformMap[fileType] || { name: fileType, color: 'gray', icon: null };
        return <Tag color={platform.color}>{platform.icon}{platform.name}</Tag>;
      },
    },
    {
      title: '面板版本',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (version: string) => <span className="text-purple-600 font-medium">v{version}</span>,
    },
    {
      title: '面板ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <span className="font-mono text-sm">{id}</span>,
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 120,
      render: (size: number | null | undefined) => {
        if (size == null) return <span className="text-gray-400">—</span>;
        let sizeText = '';
        if (size < 1024) sizeText = `${size} B`;
        else if (size < 1024 * 1024) sizeText = `${(size / 1024).toFixed(1)} KB`;
        else sizeText = `${(size / (1024 * 1024)).toFixed(1)} MB`;
        return <span>{sizeText}</span>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100, // 调整宽度以适应新按钮
      fixed: 'right',
      render: (_: unknown, record: PanelTemplate) => (
        <Space size="small">
          <Button
            size="small"
            icon={<UpOutlined className="text-xs" />}
            onClick={() => navigate(`/panels/${record.id}/update`)}
            className="hover:shadow-sm transition-shadow"
          >
            升级
          </Button>
          {/* <Button
            size="small"
            icon={<PictureOutlined className="text-xs" />}
            onClick={() => handleManageImages(record.id)}
            className="hover:shadow-sm transition-shadow"
          >
            图片管理
          </Button> */}
        </Space>
      ),
    },
  ];

  // ✅ 关键：配置受控分页
  const paginationConfig: TablePaginationConfig = {
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
    showTotal: (total) => `共 ${total} 个面板文件`,
    onShowSizeChange: (_, size) => {
      setPageSize(size);
    },
    // 注意：因为是前端分页且只有一整页数据，不需要处理页码切换（current）
  };

  return (
    <div className="w-full space-y-8">
      {/* 标题+操作区 - 适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">面板管理</h1>
          <p className="text-gray-500 mt-1">
            管理iOS/Android端面板文件，支持上传、升级及删除。
          </p>
        </div>
        <Button
          type="primary"
          onClick={handleUpload}
          icon={<UploadOutlined />}
          className="px-6 hover:shadow-md transition-shadow"
        >
          上传面板
        </Button>
      </div>

      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
        <Table
          dataSource={panels}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={paginationConfig}
          bordered={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          style={{
            '--ant-table-row-hover-bg': '#f5faff',
            '--ant-table-header-text-color': '#667085',
            '--ant-table-border-color': '#e8e8e8',
          } as React.CSSProperties}
          locale={{
            emptyText: '暂无面板数据，点击右上角「上传面板」添加第一个面板文件吧。',
          }}
        />
      </Card>
    </div>
  );
};